import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import { isLocationActive } from "@/common/Methods";
import { ListItemIcon, ListItemText } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useHasPermission } from "@/hooks";

interface Level2MenuHeadProps {
  link: string;
  label: string;
  icon: ReactNode;
  feature: string;
  codes: string[];
  children?: ReactNode;
}

export const Level2MenuHead = ({
  link,
  label,
  icon,
  children,
  codes,
  feature,
}: Level2MenuHeadProps): ReactElement | null => {
  const location = useLocation();
  const { allow } = useHasPermission(codes, feature);

  const isActive = useMemo(() => {
    return isLocationActive(location, link);
  }, [link, location]);

  const activeColor = useMemo(() => {
    return isActive ? "leftMenu.textColorActive" : "leftMenu.textColorInActive";
  }, [isActive]);

  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive]);

  const handleClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  if (!allow) {
    return null;
  }

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          color: activeColor,
        }}
      >
        <ListItemButton
          onClick={handleClick}
          disableGutters
          sx={(theme) => ({
            p: 1,
            borderRadius: "8px",
            backgroundColor: isActive
              ? alpha(theme.palette.leftMenu.backgroundColorSelected, 0.28)
              : "transparent",
          })}
        >
          <ListItemIcon
            sx={{
              minWidth: "25px",
              color: activeColor,
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText primary={label} />
        </ListItemButton>
      </ListItem>

      <Collapse in={open} timeout="auto">
        <List component="div">{children}</List>
      </Collapse>
    </>
  );
};
